import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { DeleteButton, EditButton, List, ShowButton } from "@refinedev/antd";
import { useCreate, useInvalidate } from "@refinedev/core";
import { ConfigProvider, Flex, message, Table, TableColumnsType, Typography } from "antd";
import { LoadingSpinner } from "components/LoadingSpinner";
import { useSortableTable } from "hooks/useSortableTables";
import { useEffect } from "react";
import { COLOR_PRIMARY_ORANGE } from "theme/colors";

export const DocumentsList: React.FC = () => {
  const ActionsComponents = (id: string) => {
    return (
      <Flex gap={4} justify="end">
        <ShowButton hideText size="small" type="text" recordItemId={id} />
        <EditButton
          style={{ color: "#ff5a17" }}
          hideText
          size="small"
          type="text"
          recordItemId={id}
        />
        <ConfigProvider
          theme={{
            components: {
              Button: {
                colorBgContainer: "transparent",
                textHoverBg: "rgba(255, 255, 255, 0.06) !important"
              }
            }
          }}>
          <DeleteButton
            type="text"
            resource="document"
            style={{ color: "red" }}
            hideText
            size="small"
            recordItemId={id}
            confirmTitle={"Are you sure you want to delete this document?"}
            confirmOkText={"Yes"}
            confirmCancelText={"Cancel"}
            onSuccess={() => {
              message.success("Document deleted successfully");
            }}
            onError={() => {
              message.error("Failed to delete document");
            }}
          />
        </ConfigProvider>
      </Flex>
    );
  };

  const defaultColumnData: TableColumnsType<any> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text: string) => (
        <Typography.Text
          style={{ maxWidth: "500px" }}
          ellipsis={{
            tooltip: text
          }}>
          {text}
        </Typography.Text>
      )
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (record: any) => ActionsComponents(record.id)
    }
  ];

  const { tableQueryResult, tableProps, isReordered, dndContextProps, sortableContextProps } =
    useSortableTable<any>({
      resource: "document",
      rowKey: "id",
      pagination: {
        mode: "off"
      },
      onSearch: (values: any) => {
        return [
          {
            field: "title",
            operator: "eq",
            value: values.title
          }
        ];
      }
    });

  const invalidate = useInvalidate();
  const { mutate: updateOrder, isLoading: isUpdatingOrder } = useCreate({
    successNotification: {
      type: "success",
      description: "Success",
      message: "Documents reordered successfully"
    }
  });

  // update remote order when reordered
  useEffect(() => {
    if (tableProps.dataSource && isReordered) {
      updateOrder(
        {
          resource: "document/reorder",
          values: {
            order: tableProps.dataSource.map((item: any) => item.id)
          }
        },
        {
          onSettled() {
            invalidate({
              resource: "document",
              invalidates: ["all"]
            });
          }
        }
      );
    }
  }, [invalidate, isReordered, tableProps.dataSource]);

  // if (tableQueryResult.isLoading) return <LoadingSpinner />;

  return (
    <List
      title="Documents"
      canCreate={true}
      breadcrumb={false}
      createButtonProps={{
        children: "Create",
        style: {
          backgroundColor: COLOR_PRIMARY_ORANGE,
          color: "#fff",
          border: "none"
        }
      }}>
      <DndContext {...dndContextProps}>
        <SortableContext {...sortableContextProps}>
          <Table
            {...tableProps}
            loading={tableQueryResult.isFetching || isUpdatingOrder}
            pagination={false}
            columns={defaultColumnData}
            rowKey="id"
            className="table-list-cs"
          />
        </SortableContext>
      </DndContext>
    </List>
  );
};
